import { MATRIX_POINT_COLORS } from '@/api/groups-matrix'
import type { MatrixOfDestinyDTO } from '@/api/schema/matrix'
import { Col, Row } from '@/app/components/Flex/Flex'
import { Link } from '@/app/components/Link/Link'
import { Matrix } from '@/app/components/Matrix/Matrix'
import { Num } from '@/app/components/Num/Num'
import { Text } from '@/app/components/Text/Text'
import { UI } from '@/app/components/UI/UI'
import type { FunctionalComponent } from 'preact'
import { useState } from 'preact/hooks'
import styles from './index.module.scss'

const matrix: MatrixOfDestinyDTO = {
  'dayArcana': 9,
  'monthArcana': 7,
  'yearArcana': 8,
  'karmaArcana': 6,
  'center': 3,
  'fatherLine': 16,
  'motherLine': 15,
  'future': 14,
  'past': 15,
  'centerRod': 6,
  'f2': 22,
  'g2': 21,
  'h2': 20,
  'i2': 21,
  'f1': 11,
  'g1': 9,
  'h1': 7,
  'i1': 9,
  'personality': 12,
  'skyTop': 10,
  'forWorld': 11,
  'skyBottom': 9,
  'personalityOuter': 21,
  'skyTopOuter': 17,
  'forWorldOuter': 19,
  'skyBottomOuter': 15,
  'personalityInner': 15,
  'skyTopInner': 13,
  'heavenLine': 13,
  'earthLine': 17,
  'personalPurpose': 3,
  'fatherPurpose': 3,
  'motherPurpose': 3,
  'socialPurpose': 6,
  'spiritualPurpose': 9,
  'material': 4,
  'spiritual': 11,
  'comfort': 19,
  'money': 18,
  'balanceCenter': 20,
  'moneyCenter': 4,
  'relationshipCenter': 11,
}

const sections: {
  name: string
  desc: string
  points: (keyof MatrixOfDestinyDTO)[]
}[] = [{
  name: 'Главный аркан',
  desc: 'Твоя база: характер, сильные стороны и качества, которые чаще всего проявляются в жизни.',
  points: ['center'],
}, {
  name: 'Энергии рождения',
  desc: 'День и год рождения задают ключевые темы жизни, через которые ты растёшь и меняешься.',
  points: ['dayArcana', 'yearArcana'],
}, {
  name: 'Предназначение',
  desc: 'Здесь видно, к чему тебе важно прийти, где искать своё дело и в чём твоя главная задача.',
  points: ['spiritualPurpose'],
}, {
  name: 'Небесный канал',
  desc: 'Твоя интуиция, внутренние ориентиры и способности, которые сложно объяснить одной логикой.',
  points: ['skyTop', 'skyTopInner', 'skyTopOuter'],
}, {
  name: 'Отношения',
  desc: 'Какие отношения тебе нужны, кого ты выбираешь и какие сценарии могут повторяться в любви.',
  points: ['relationshipCenter'],
}, {
  name: 'Денежный код',
  desc: 'Через что проще зарабатывать, где теряются деньги и какие возможности стоит использовать.',
  points: ['moneyCenter'],
}]

export const MatrixSection: FunctionalComponent = () => {
  const [visiblePoints, setVisiblePoints] = useState<(keyof MatrixOfDestinyDTO)[] | null>(null)

  return (
    <UI.Wrapper>
      <UI.Holder maxWidth={600}>
        <Row
          gap={30}
          gapMobile={25}
        >
          <div class={styles.matrix}>
            <Matrix
              outer
              outerStroke='#111'
              outerStrokeWidth='2'
              visiblePoints={visiblePoints}
              matrix={matrix}
            />
          </div>

          <Row
            gap={20}
            gapMobile={15}
          >
            <Text
              as='h2'
              fontSize={34}
              fontSizeMobile={24}
              lineHeight={1}
              fontWeight={500}
              textAlign='center'
            >
              Матрица судьбы
            </Text>

            <Text
              as='p'
              fontSize={20}
              fontSizeMobile={16}
              lineHeight={1.25}
              textAlign='center'
            >
              Разберём все числа твоей Матрицы — от&nbsp;талантов&nbsp;до&nbsp;кармических задач и предназначения
            </Text>
          </Row>

          <Row
            gap={20}
            gapMobile={15}
          >
            <div class={styles.grid}>
              {sections.map(section => (
                <UI.Panel
                  hover
                  class={styles.item}
                  onPointerEnter={() => {
                    setVisiblePoints(section.points)
                  }}
                  onPointerLeave={() => {
                    setVisiblePoints(null)
                  }}
                  link='~/tools?redirect=~/tools/matrix'
                >
                  <Row
                    gap={5}
                  >
                    <Col alignItems='center' justifyContent='space-between'>
                      <Text
                        as='h4'
                        fontSize={18}
                        fontSizeMobile={16}
                        lineHeight={1}
                        fontWeight={600}
                      >
                        {section.name}
                      </Text>

                      <Col>
                        {section.points.map(point => (
                          <Num color={MATRIX_POINT_COLORS[point]} size='l'>
                            {matrix[point]}
                          </Num>
                        ))}
                      </Col>
                    </Col>

                    <Text
                      as='p'
                      fontSize={16}
                      fontSizeMobile={14}
                      opacity={0.5}
                    >
                      {section.desc}
                    </Text>
                  </Row>
                </UI.Panel>
              ))}
            </div>

            <UI.Panel>
              <Row
                gap={10}
                gapMobile={5}
              >
                <Text
                  as='h3'
                  fontSize={18}
                  fontSizeMobile={16}
                  fontWeight={600}
                >
                  Разборы арканов
                </Text>

                <Text
                  as='p'
                  fontSize={16}
                  fontSizeMobile={14}
                >
                  <Link to='/guide/matrix-arcana' class='thin-link'>
                    Арканы Матрицы судьбы
                  </Link>
                </Text>

                <Text
                  as='p'
                  fontSize={16}
                  fontSizeMobile={14}
                >
                  <Link to='/guide/matrix-money' class='thin-link'>
                    Арканы денежного кода
                  </Link>
                </Text>
              </Row>
            </UI.Panel>

            <UI.Button
              block
              size='l'
              type='primary'
              link='~/tools?redirect=~/tools/matrix'
            >
              Рассчитать матрицу
            </UI.Button>
          </Row>
        </Row>
      </UI.Holder>
    </UI.Wrapper>
  )
}
